<template>
    <div>
        <div class="kgr-grid kgr-grid--vertical">
            <div class="kgr-grid__cell">
                <div class="kgr-row">
                    <h1>{{ $t('club.stocktaking.member.referenceDate.title') }}</h1>
                </div>
                <div class="kgr-float kgr-float--left kgr-float--inText">
                    <div class="kgr-inputGroup">
                        <label class="kgr-inputGroup__label">{{ $t('Stichtag') }}</label>
                        <kgr-date-picker
                            v-model.trim="date"
                            :value="date"
                            @post-input="loadMembershipDemographics"
                            @error="invalidDate"
                        />
                    </div>
                </div>
                <div
                    v-if="showModeDropdown"
                    class="kgr-float kgr-float--left kgr-float--inText"
                >
                    <div class="kgr-inputGroup">
                        <label class="kgr-inputGroup__label">{{ $t('Altersverteilung') }}</label>
                        <select
                            v-model="mode"
                            class="kgr-input"
                            @change="loadMembershipDemographics"
                        >
                            <option value="default">
                                {{ $t('club.stocktaking.mode.default') }}
                            </option>
                            <option value="alternative">
                                {{ $t('0 – 5, …, 80+') }}
                            </option>
                        </select>
                    </div>
                </div>
            </div>
            <kgr-loading v-if="loading" />
            <kgr-empty-data
                v-if="empty && !loading"
                :value="$t('Keine Daten vorhanden')"
            />
            <div
                v-if="!empty"
                class="kgr-grid__cell kgr-row"
            >
                <table class="kgr-table kgr-table--striped kgr-table--block table-groups">
                    <thead>
                        <tr class="kgr-table__row">
                            <th class="kgr-table__cell kgr-table__cell--headline"></th>
                            <th
                                v-for="group in currentAgeGroups"
                                :key="group.translationKey"
                                class="kgr-table__cell kgr-table__cell--headline kgr-table__cell--centered"
                                colspan="4"
                            >
                                {{ $t(group.translationKey) }}
                            </th>
                        </tr>
                        <tr>
                            <th class="kgr-table__cell kgr-table__cell--headline">
                                {{ $t('club.stocktaking.export.department') }}
                            </th>
                            <template
                                v-for="group in currentAgeGroups"
                                :key="`sub-${group.translationKey}`"
                            >
                                <th class="kgr-table__cell kgr-table__cell--headline">
                                    {{ $t('club.stocktaking.export.gender.m') }}
                                </th>
                                <th class="kgr-table__cell kgr-table__cell--headline">
                                    {{ $t('club.stocktaking.export.gender.f') }}
                                </th>
                                <th class="kgr-table__cell kgr-table__cell--headline">
                                    {{ $t('club.stocktaking.export.gender.v') }}
                                </th>
                                <th class="kgr-table__cell kgr-table__cell--headline">
                                    {{ $t('club.stocktaking.export.g') }}
                                </th>
                            </template>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="(value, departmentName) in data"
                            :key="departmentName"
                            class="kgr-table__row  kgr-table__row--top"
                        >
                            <td class="kgr-table__cell">
                                {{ departmentName }}
                            </td>
                            <template
                                v-for="row in value"
                                :key="departmentName + row.name"
                            >
                                <td class="kgr-table__cell">
                                    {{ row.calc['gender.m'] }}
                                </td>
                                <td class="kgr-table__cell">
                                    {{ row.calc['gender.f'] }}
                                </td>
                                <td class="kgr-table__cell">
                                    {{ row.calc['gender.v'] }}
                                </td>
                                <td class="kgr-table__cell">
                                    {{ row.total }}
                                </td>
                            </template>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="kgr-grid__cell">
                <div class="kgr-float kgr-flow--clear kgr-row">
                    <div class="kgr-float kgr-float--left kgr-float--inText">
                        <kgr-button
                            v-if="!loading"
                            :disabled="pendingExport || empty"
                            @click="download"
                        >
                            {{ $t('Download') }}
                        </kgr-button>
                    </div>
                    <div class="kgr-float kgr-float--left kgr-float--inText">
                        <kgr-button
                            v-if="!loading"
                            :disabled="jobRunning || date === null"
                            @click="createUpdateJob"
                        >
                            {{ $t('Aktualisieren') }}
                        </kgr-button>
                    </div>
                    <span v-if="jobRunning">{{ $t('Die Daten werden im Hintergrund berechnet. Die Grafik aktualisiert sich sobald die Daten verfügbar sind.') }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ClubStatsRepository from '../../../js/repository/ClubStatsRepository';
import Date from '../../../js/date/Date';
import DatePicker from '../../common/form/picker/DateObjectPicker.vue';
import EmptyData from '../../common/EmptyData.vue';
import KgrButton from '../../common/form/button/KgrButton.vue';
import Loading from '../../common/Loading.vue';

const AGE_GROUPS = {
    default: [
        { translationKey: 'club.stocktaking.export.0to6' },
        { translationKey: 'club.stocktaking.export.7to14' },
        { translationKey: 'club.stocktaking.export.15to18' },
        { translationKey: 'club.stocktaking.export.19to26' },
        { translationKey: 'club.stocktaking.export.27to40' },
        { translationKey: 'club.stocktaking.export.41to60' },
        { translationKey: 'club.stocktaking.export.61+' },
        { translationKey: 'club.stocktaking.export.total' },
    ],
    alternative: [
        { translationKey: 'club.stocktaking.export.0to5' },
        { translationKey: 'club.stocktaking.export.6to10' },
        { translationKey: 'club.stocktaking.export.11to14' },
        { translationKey: 'club.stocktaking.export.15to18' },
        { translationKey: 'club.stocktaking.export.19to27' },
        { translationKey: 'club.stocktaking.export.28to40' },
        { translationKey: 'club.stocktaking.export.41to55' },
        { translationKey: 'club.stocktaking.export.56to67' },
        { translationKey: 'club.stocktaking.export.68to79' },
        { translationKey: 'club.stocktaking.export.80+' },
        { translationKey: 'club.stocktaking.export.total' },
    ],
};

export default {
    name: 'MemberStocktaking',
    components: {
        'kgr-date-picker': DatePicker,
        'kgr-empty-data': EmptyData,
        'kgr-loading': Loading,
        'kgr-button': KgrButton,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            empty: true,
            loading: true,
            jobRunning: false,
            date: Date.today(),
            mode: this.clubId === 161 ? (localStorage.getItem('memberStocktaking.mode') || 'default') : 'default',
            data: null,
            pendingExport: false,
        };
    },
    computed: {
        showModeDropdown() {
            return this.clubId === 161;
        },
        currentAgeGroups() {
            return AGE_GROUPS[this.mode] || AGE_GROUPS.default;
        },
    },
    watch: {
        mode(val) {
            localStorage.setItem('memberStocktaking.mode', val);
        },
    },
    mounted() {
        this.loadMembershipDemographics();
    },
    methods: {
        invalidDate() {
            this.date = null;
        },
        loadMembershipDemographics(date) {
            if (date === null) {
                this.invalidDate();
                return;
            }
            ClubStatsRepository.getMembershipDemographicsForAll(this.clubId, this.date.format('YYYY-MM-DD'), this.mode)
                .then((stats) => {
                    this.empty = !stats || Object.keys(stats).length === 0;
                    this.loading = false;
                    this.data = stats;
                });
        },
        loadRunningJob() {
            ClubStatsRepository.getMembershipDemographicsForAllJob(this.clubId, this.date.format('YYYY-MM-DD'), this.mode)
                .then((response) => {
                    this.jobRunning = response.isRunning;
                    if (this.jobRunning === false) {
                        this.loadMembershipDemographics();
                    }
                })
                .finally(() => {
                    if (this.jobRunning === true) {
                        setTimeout(() => {
                            this.loadRunningJob();
                        }, 1000);
                    }
                });
        },
        createUpdateJob() {
            ClubStatsRepository.putMembershipDemographicsForAllJob(this.clubId, this.date.format('YYYY-MM-DD'), this.mode)
                .then(() => {
                    this.jobRunning = true;
                    this.loadRunningJob();
                });
        },
        download() {
            this.pendingExport = true;
            ClubStatsRepository.getMembershipDemographicsForAllXlsx(this.clubId, this.date.format('YYYY-MM-DD'), this.mode)
                .then(() => {})
                .catch(() => this.$notify.error('error.loadingData'))
                .finally(() => this.pendingExport = false);
        },
    },
};
</script>

<style scoped>

</style>
